import type { ElkExtendedEdge, ElkNode, ElkPort, LayoutOptions } from 'elkjs/lib/elk-api';
import type { Edge, Node, NodeGroup, Point, Size, SocketDefinition } from '../types';
import { isMarkupType } from '../constants/nodeTypes';
import { simplifyPoints } from './polyline';

/**
 * Layout through ELK's layered algorithm.
 *
 * The whole compound graph - frames, their contents, and the edges that cross
 * between them - goes to ELK in one call. That is the point of this module.
 * The engine it replaces laid out each frame on its own and then arranged the
 * frames, so no ordering decision could ever see past a frame boundary and the
 * long edges between frames were routed after every position was already
 * fixed. Ordering, frame placement, port assignment and routing are one problem
 * and ELK solves them together.
 *
 * What comes back is converted into VeloStruct's own model - node positions,
 * frame bounds, socket positions, and a polyline per edge - and nothing about
 * rendering, styling or interaction changes.
 */

export type RankAxis = 'ltr' | 'ttb';

/**
 * Every gap is a multiple of one unit so the whitespace reads as rhythm rather
 * than as a pile of unrelated constants. Edges are spaced one unit from each
 * other and one and a half from anything solid, which is what makes a bundle
 * look deliberately parallel instead of merely non-overlapping.
 */
const UNIT = 16;
const SPACING = {
    /** Between two boxes sharing a layer. */
    nodeNode: UNIT * 2,
    /** Between one layer and the next. */
    layerLayer: UNIT * 3,
    /** Between two edges running side by side. */
    edgeEdge: UNIT,
    /** Between an edge and any box or frame it passes. */
    edgeNode: UNIT * 1.5,
    /** Between two disconnected parts of the graph. */
    componentComponent: UNIT * 4,
    /** Inside a frame: room for the title bar on top, an even margin elsewhere. */
    framePadTop: 34,
    framePad: UNIT * 2,
    /** Smallest a frame may be drawn, so an empty one is still a box. */
    frameMinWidth: UNIT * 10,
    frameMinHeight: UNIT * 6,
};

export interface FrameBounds {
    position: Point;
    size: Size;
}

/** Where one edge attaches, and how it gets from one end to the other. */
export interface EdgeRoute {
    /** Absolute point on the source node's border. */
    source: Point;
    /** Absolute point on the target node's border. */
    target: Point;
    /** Absolute corners between the two, source end first. */
    bends: Point[];
}

export interface SocketPlacement {
    nodeId: string;
    socketId: string;
    /** Position relative to the node's top-left corner. */
    position: Point;
    side: 'left' | 'right' | 'up' | 'down';
}

export interface FlowLayoutResult {
    positions: Record<string, Point>;
    frames: Record<string, FrameBounds>;
    routes: Record<string, EdgeRoute>;
    sockets: SocketPlacement[];
    /**
     * The box the whole layout occupies. ELK reports it on the root node, which
     * is the only place that knows about every box, frame and wire at once -
     * `positions` alone is a set of corners and would centre the diagram half a
     * node off.
     */
    bounds: { x: number; y: number; width: number; height: number } | null;
}

export interface FlowLayoutOptions {
    axis?: RankAxis;
    /** Overrides for the spacing constants, in px. */
    spacing?: Partial<typeof SPACING>;
}

// ── loading ────────────────────────────────────────────────────────────────

interface ElkEngine {
    layout(graph: ElkNode): Promise<ElkNode>;
}

let engine: Promise<ElkEngine> | null = null;

/**
 * ELK is about half a megabyte gzipped, so it is fetched the first time a
 * layout is actually asked for rather than at start-up. One instance is kept:
 * it holds no state between runs, and constructing it is the expensive part.
 */
function getEngine(): Promise<ElkEngine> {
    if (!engine) {
        engine = import('elkjs/lib/elk.bundled.js').then(module => {
            const Ctor = (module.default ?? module) as unknown as new () => ElkEngine;
            return new Ctor();
        });
    }
    return engine;
}

// ── graph construction ─────────────────────────────────────────────────────

const SIDE_BY_AXIS: Record<RankAxis, { input: string; output: string }> = {
    ttb: { input: 'NORTH', output: 'SOUTH' },
    ltr: { input: 'WEST', output: 'EAST' },
};

const TICK_BY_SIDE: Record<string, SocketPlacement['side']> = {
    NORTH: 'up',
    SOUTH: 'down',
    WEST: 'left',
    EAST: 'right',
};

const portId = (nodeId: string, socketId: string) => `${nodeId}\u0000${socketId}`;

function rootOptions(axis: RankAxis, spacing: typeof SPACING): LayoutOptions {
    return {
        'elk.algorithm': 'layered',
        'elk.direction': axis === 'ttb' ? 'DOWN' : 'RIGHT',
        'elk.edgeRouting': 'ORTHOGONAL',
        // The one setting this whole module exists for: order and place the
        // contents of every frame in the same sweep as the frames themselves.
        'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
        'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
        // Sweeps of the crossing minimiser. The default of 7 leaves crossings
        // on a graph this size that another twenty sweeps find; the whole run
        // is a fraction of a second either way.
        'elk.layered.thoroughness': '30',
        'elk.spacing.nodeNode': String(spacing.nodeNode),
        'elk.layered.spacing.nodeNodeBetweenLayers': String(spacing.layerLayer),
        'elk.spacing.edgeEdge': String(spacing.edgeEdge),
        'elk.layered.spacing.edgeEdgeBetweenLayers': String(spacing.edgeEdge),
        'elk.spacing.edgeNode': String(spacing.edgeNode),
        'elk.layered.spacing.edgeNodeBetweenLayers': String(spacing.edgeNode),
        'elk.spacing.componentComponent': String(spacing.componentComponent),
        'elk.separateConnectedComponents': 'true',
        // Absolute coordinates for every edge section, whatever depth the edge
        // was declared at. Without this each section is relative to a container
        // that has to be worked out from the edge's own hierarchy.
        'elk.json.edgeCoords': 'ROOT',
    };
}

function frameOptions(spacing: typeof SPACING): LayoutOptions {
    return {
        'elk.padding': `[top=${spacing.framePadTop},left=${spacing.framePad},bottom=${spacing.framePad},right=${spacing.framePad}]`,
        // A frame with nothing in it is sized to nothing, and a frame drawn at
        // zero by zero is a frame the author cannot find again to drop
        // something into. The floor also reserves the room, so the frames
        // around it are placed as though it were there.
        'elk.nodeSize.constraints': 'MINIMUM_SIZE',
        'elk.nodeSize.minimum': `(${spacing.frameMinWidth},${spacing.frameMinHeight})`,
    };
}

interface Membership {
    /** Group each node sits directly inside, or null for the top level. */
    parentOfNode: Map<string, string | null>;
    /** Group each group sits directly inside, or null for the top level. */
    parentOfGroup: Map<string, string | null>;
}

function membershipOf(nodes: Node[], groups: NodeGroup[]): Membership {
    const known = new Set(groups.map(group => group.id));
    const parentOfNode = new Map<string, string | null>();
    const parentOfGroup = new Map<string, string | null>();
    for (const group of groups) {
        parentOfGroup.set(group.id, group.parentGroupId && known.has(group.parentGroupId) ? group.parentGroupId : null);
    }
    for (const node of nodes) {
        parentOfNode.set(node.id, node.parentGroupId && known.has(node.parentGroupId) ? node.parentGroupId : null);
    }
    // A group's own list wins: it is the side of the relationship the grouping
    // actions maintain, and a node can be left pointing at a frame that no
    // longer claims it.
    for (const group of groups) {
        for (const childId of group.childNodeIds) {
            if (parentOfNode.has(childId)) parentOfNode.set(childId, group.id);
        }
        for (const childId of group.childGroupIds) {
            if (parentOfGroup.has(childId) && childId !== group.id) parentOfGroup.set(childId, group.id);
        }
    }
    // Break any cycle a bad edit could have left behind, or the recursion that
    // builds the ELK tree would never terminate.
    for (const group of groups) {
        const seen = new Set<string>([group.id]);
        let cursor = parentOfGroup.get(group.id) ?? null;
        while (cursor) {
            if (seen.has(cursor)) {
                parentOfGroup.set(group.id, null);
                break;
            }
            seen.add(cursor);
            cursor = parentOfGroup.get(cursor) ?? null;
        }
    }
    return { parentOfNode, parentOfGroup };
}

/**
 * The chain of frames an element sits inside, outermost first. Used to find the
 * innermost frame that contains both ends of an edge, which is where ELK wants
 * that edge declared.
 */
function ancestryOf(id: string, membership: Membership): string[] {
    const chain: string[] = [];
    let cursor = membership.parentOfNode.has(id)
        ? membership.parentOfNode.get(id)!
        : membership.parentOfGroup.get(id) ?? null;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
        seen.add(cursor);
        chain.push(cursor);
        cursor = membership.parentOfGroup.get(cursor) ?? null;
    }
    return chain.reverse();
}

const ROOT = 'elk-root';

function commonContainer(a: string, b: string, membership: Membership): string {
    const chainA = ancestryOf(a, membership);
    const chainB = ancestryOf(b, membership);
    let i = 0;
    while (i < chainA.length && i < chainB.length && chainA[i] === chainB[i]) i++;
    return i === 0 ? ROOT : chainA[i - 1];
}

/** The sockets each node actually needs a port for, and which side it goes on. */
function portsFor(
    node: Node,
    used: Map<string, Set<string>>,
    axis: RankAxis,
    socketById: Map<string, SocketDefinition>,
): ElkPort[] {
    const sides = SIDE_BY_AXIS[axis];
    const wanted = used.get(node.id);
    if (!wanted) return [];
    const ports: ElkPort[] = [];
    for (const socketId of wanted) {
        const socket = socketById.get(`${node.id}\u0000${socketId}`);
        if (!socket) continue;
        ports.push({
            id: portId(node.id, socketId),
            width: 0,
            height: 0,
            layoutOptions: { 'elk.port.side': socket.type === 'input' ? sides.input : sides.output },
        });
    }
    return ports;
}

// ── the layout run ─────────────────────────────────────────────────────────

/**
 * Lay out a flow graph. Nothing is mutated; the caller decides what to keep.
 *
 * Markup nodes - arrows, notes, free labels - are left where the author put
 * them and take no part in the ranking, along with any edge that touches one.
 */
export async function layoutFlowGraph(
    nodes: Node[],
    edges: Edge[],
    groups: NodeGroup[],
    options: FlowLayoutOptions = {},
): Promise<FlowLayoutResult> {
    const axis = options.axis ?? 'ttb';
    const spacing = { ...SPACING, ...options.spacing };
    const empty: FlowLayoutResult = { positions: {}, frames: {}, routes: {}, sockets: [], bounds: null };

    const laidOut = nodes.filter(node => !isMarkupType(node.type));
    if (laidOut.length === 0) return empty;
    const nodeById = new Map(laidOut.map(node => [node.id, node]));
    const socketById = new Map<string, SocketDefinition>();
    for (const node of laidOut) {
        for (const socket of node.sockets) socketById.set(`${node.id}\u0000${socket.id}`, socket);
    }

    const routable = edges.filter(edge =>
        nodeById.has(edge.sourceNodeId) && nodeById.has(edge.targetNodeId));
    const membership = membershipOf(laidOut, groups);

    // Only sockets an edge actually uses become ports. A node's unused sockets
    // would otherwise take up room along the side and push the used ones into
    // a worse order.
    const used = new Map<string, Set<string>>();
    const markSocketAsUsed = (nodeId: string, socketId: string) => {
        const set = used.get(nodeId) ?? new Set<string>();
        set.add(socketId);
        used.set(nodeId, set);
    };
    for (const edge of routable) {
        if (socketById.has(`${edge.sourceNodeId}\u0000${edge.sourceSocketId}`)) {
            markSocketAsUsed(edge.sourceNodeId, edge.sourceSocketId);
        }
        if (socketById.has(`${edge.targetNodeId}\u0000${edge.targetSocketId}`)) {
            markSocketAsUsed(edge.targetNodeId, edge.targetSocketId);
        }
    }

    const containers = new Map<string, ElkNode>();
    const root: ElkNode = {
        id: ROOT,
        layoutOptions: rootOptions(axis, spacing),
        children: [],
        edges: [],
    };
    containers.set(ROOT, root);
    for (const group of groups) {
        containers.set(group.id, {
            id: group.id,
            layoutOptions: frameOptions(spacing),
            children: [],
            edges: [],
        });
    }
    for (const group of groups) {
        const parent = containers.get(membership.parentOfGroup.get(group.id) ?? ROOT) ?? root;
        parent.children!.push(containers.get(group.id)!);
    }
    for (const node of laidOut) {
        const parent = containers.get(membership.parentOfNode.get(node.id) ?? ROOT) ?? root;
        const ports = portsFor(node, used, axis, socketById);
        parent.children!.push({
            id: node.id,
            width: node.size.width,
            height: node.size.height,
            ports,
            // Sides are ours - an input belongs on the face the flow arrives at
            // - but which port sits where along that side is ELK's to decide,
            // and it decides it by counting crossings.
            layoutOptions: ports.length > 0 ? { 'elk.portConstraints': 'FIXED_SIDE' } : {},
        });
    }

    for (const edge of routable) {
        const container = containers.get(commonContainer(edge.sourceNodeId, edge.targetNodeId, membership)) ?? root;
        const hasSourcePort = used.get(edge.sourceNodeId)?.has(edge.sourceSocketId);
        const hasTargetPort = used.get(edge.targetNodeId)?.has(edge.targetSocketId);
        const elkEdge: ElkExtendedEdge = {
            id: edge.id,
            sources: [hasSourcePort ? portId(edge.sourceNodeId, edge.sourceSocketId) : edge.sourceNodeId],
            targets: [hasTargetPort ? portId(edge.targetNodeId, edge.targetSocketId) : edge.targetNodeId],
        };
        container.edges!.push(elkEdge);
    }

    const elk = await getEngine();
    const result = await elk.layout(root);

    // ── read the answer back ───────────────────────────────────────────────
    const positions: Record<string, Point> = {};
    const frames: Record<string, FrameBounds> = {};
    const sockets: SocketPlacement[] = [];
    const groupIds = new Set(groups.map(group => group.id));

    const walk = (container: ElkNode, originX: number, originY: number) => {
        for (const child of container.children ?? []) {
            const x = originX + (child.x ?? 0);
            const y = originY + (child.y ?? 0);
            if (groupIds.has(child.id)) {
                frames[child.id] = {
                    position: { x, y },
                    size: { width: child.width ?? 0, height: child.height ?? 0 },
                };
                walk(child, x, y);
                continue;
            }
            if (!nodeById.has(child.id)) continue;
            positions[child.id] = { x, y };
            for (const port of child.ports ?? []) {
                const socketId = port.id.slice(child.id.length + 1);
                const side = port.layoutOptions?.['elk.port.side'];
                sockets.push({
                    nodeId: child.id,
                    socketId,
                    position: { x: port.x ?? 0, y: port.y ?? 0 },
                    side: TICK_BY_SIDE[side ?? ''] ?? sideFromPosition(port, child),
                });
            }
        }
    };
    walk(result, 0, 0);

    const routes: Record<string, EdgeRoute> = {};
    const collect = (container: ElkNode) => {
        for (const elkEdge of (container.edges ?? []) as ElkExtendedEdge[]) {
            const sections = elkEdge.sections ?? [];
            if (sections.length === 0) continue;
            const first = sections[0];
            const last = sections[sections.length - 1];
            const bends: Point[] = [];
            for (const section of sections) {
                // A multi-section edge is one ELK split at a hierarchy crossing;
                // the join between two sections is a point on the route like any
                // other, so the inner endpoints are kept as bends.
                if (section !== first) bends.push({ x: section.startPoint.x, y: section.startPoint.y });
                for (const bend of section.bendPoints ?? []) bends.push({ x: bend.x, y: bend.y });
                if (section !== last) bends.push({ x: section.endPoint.x, y: section.endPoint.y });
            }
            routes[elkEdge.id] = {
                source: { x: first.startPoint.x, y: first.startPoint.y },
                target: { x: last.endPoint.x, y: last.endPoint.y },
                bends,
            };
        }
        for (const child of container.children ?? []) collect(child);
    };
    collect(result);

    const bounds = result.width !== undefined && result.height !== undefined
        ? { x: 0, y: 0, width: result.width, height: result.height }
        : null;

    return { positions, frames, routes, sockets, bounds };
}

/** Fallback when ELK echoes no side back: read it off where the port landed. */
function sideFromPosition(port: ElkPort, node: ElkNode): SocketPlacement['side'] {
    const x = port.x ?? 0;
    const y = port.y ?? 0;
    const w = node.width ?? 0;
    const h = node.height ?? 0;
    const distances: [SocketPlacement['side'], number][] = [
        ['left', x],
        ['right', Math.abs(w - x)],
        ['up', y],
        ['down', Math.abs(h - y)],
    ];
    distances.sort((a, b) => a[1] - b[1]);
    return distances[0][0];
}

// ── moving a finished layout around ────────────────────────────────────────

/**
 * Shift a whole layout. Socket positions are relative to their node, so only
 * the absolute halves - positions, frame corners, and every point on a route -
 * move.
 */
export function translateLayout(layout: FlowLayoutResult, dx: number, dy: number): FlowLayoutResult {
    if (dx === 0 && dy === 0) return layout;
    const shift = (point: Point): Point => ({ x: point.x + dx, y: point.y + dy });
    const positions: Record<string, Point> = {};
    for (const [id, position] of Object.entries(layout.positions)) positions[id] = shift(position);
    const frames: Record<string, FrameBounds> = {};
    for (const [id, frame] of Object.entries(layout.frames)) {
        frames[id] = { position: shift(frame.position), size: frame.size };
    }
    const routes: Record<string, EdgeRoute> = {};
    for (const [id, route] of Object.entries(layout.routes)) {
        routes[id] = {
            source: shift(route.source),
            target: shift(route.target),
            bends: route.bends.map(shift),
        };
    }
    return {
        positions,
        frames,
        routes,
        sockets: layout.sockets,
        bounds: layout.bounds
            ? { ...layout.bounds, x: layout.bounds.x + dx, y: layout.bounds.y + dy }
            : null,
    };
}

// ── applying it ────────────────────────────────────────────────────────────

/**
 * Move a graph onto a layout: node positions, frame bounds, socket positions
 * and one route per edge.
 *
 * The route is stored with the two endpoints it was computed against, so it
 * can tell later whether it is still describing the diagram it was made for.
 * That is what keeps a saved file from reopening with wires that no longer
 * reach their nodes.
 */
export function applyFlowLayout(
    nodes: Node[],
    edges: Edge[],
    groups: NodeGroup[],
    layout: FlowLayoutResult,
): void {
    const nodeById = new Map(nodes.map(node => [node.id, node]));
    for (const node of nodes) {
        const position = layout.positions[node.id];
        if (position) node.position = { x: position.x, y: position.y };
    }
    for (const group of groups) {
        const bounds = layout.frames[group.id];
        if (!bounds) continue;
        group.position = { ...bounds.position };
        group.size = { ...bounds.size };
    }
    for (const placement of layout.sockets) {
        const node = nodeById.get(placement.nodeId);
        const socket = node?.sockets.find(candidate => candidate.id === placement.socketId);
        if (!socket) continue;
        socket.position = { x: placement.position.x, y: placement.position.y };
        // Without this the outward direction is inferred from the position, and
        // a port near a corner is read as belonging to the wrong face.
        socket.tickSide = placement.side;
    }
    for (const edge of edges) {
        const route = layout.routes[edge.id];
        if (!route) {
            delete edge.autoRoute;
            continue;
        }
        edge.autoRoute = {
            from: route.source,
            to: route.target,
            points: simplifyPoints([route.source, ...route.bends, route.target]).slice(1, -1),
        };
    }
}

/** Lay out a graph and move it onto the result in one step. */
export async function autoLayout(
    nodes: Node[],
    edges: Edge[],
    groups: NodeGroup[],
    options: FlowLayoutOptions = {},
): Promise<FlowLayoutResult> {
    const layout = await layoutFlowGraph(nodes, edges, groups, options);
    applyFlowLayout(nodes, edges, groups, layout);
    return layout;
}
